import {
  EmailSchema,
  VietnamPhoneSchema,
  collapseWhitespace,
  normalizeVietnamPhone,
  type FormField,
  type PublicLocation,
} from '@irace/shared';
import { COPY, maxLengthMessage, requiredFieldMessage } from '../copy/vi.ts';

/**
 * F-003 / §9 — local form validation.
 *
 * Kept as pure functions so the rules are testable without a DOM, and so the
 * submit path can ask "is this valid?" before it creates a submission_id.
 * §12 is explicit: no IndexedDB record is created until the local form is
 * valid.
 */

export const LOCATION_FIELD_KEY = 'location_id';

export interface LocationMode {
  mode: 'hidden' | 'fixed' | 'pick';
  selected?: PublicLocation;
}

/**
 * How much of the location control a participant should see.
 *
 * An event held at one venue should not make someone confirm the only answer
 * before they can type their name — the fastest check-in is the one with the
 * fewest taps at the door. A single-venue event still shows the venue name so
 * the participant knows the QR was the right one; an event with no venue
 * configured at all shows nothing.
 *
 * The placeholder location never appears to a participant, but it is always
 * what gets submitted when there is no real one, because `checkins.location_id`
 * is NOT NULL and every downstream report keys on it.
 */
export function chooseLocationMode(locations: readonly PublicLocation[]): LocationMode {
  const real = locations.filter((location) => !location.is_placeholder);

  if (real.length === 0) {
    const placeholder = locations.find((location) => location.is_placeholder);
    return placeholder ? { mode: 'hidden', selected: placeholder } : { mode: 'hidden' };
  }
  if (real.length === 1) {
    const [only] = real;
    return only ? { mode: 'fixed', selected: only } : { mode: 'hidden' };
  }
  return { mode: 'pick' };
}

export interface FieldIssue {
  key: string;
  message: string;
}

export type FormValues = Record<string, string>;

function validateField(field: FormField, rawValue: string | undefined): string | null {
  const value = collapseWhitespace(rawValue ?? '');

  if (value.length === 0) {
    return field.required ? requiredFieldMessage(field.label) : null;
  }
  if (field.max_length !== undefined && value.length > field.max_length) {
    return maxLengthMessage(field.max_length);
  }
  if (field.type === 'email' && !EmailSchema.safeParse(value).success) {
    return COPY.validation.invalidEmail;
  }
  // The typed spacing is normalized away before the rule is applied, and the
  // same normalized value is what `toOutboxData` saves, so what passes here is
  // exactly what the API will see.
  if (field.type === 'tel' && !VietnamPhoneSchema.safeParse(normalizeVietnamPhone(value)).success) {
    return COPY.validation.invalidPhone;
  }
  return null;
}

/** Issues for one field, used for blur-time validation (§9). */
export function validateSingleField(field: FormField, value: string | undefined): string | null {
  return validateField(field, value);
}

/**
 * Every issue in field order, so the caller can focus the first one — §9
 * requires scrolling to and focusing the first invalid field, which needs the
 * issues ordered the way the form is rendered.
 */
export function validateForm(
  fields: readonly FormField[],
  values: FormValues,
  locationId: string | null,
): FieldIssue[] {
  const issues: FieldIssue[] = [];

  if (!locationId) {
    issues.push({ key: LOCATION_FIELD_KEY, message: COPY.validation.requiredLocation });
  }

  for (const field of fields) {
    const message = validateField(field, values[field.key]);
    if (message) {
      issues.push({ key: field.key, message });
    }
  }

  return issues;
}
